import { VoucherCreate } from 'components/voucher/voucher-create/VoucherCreate'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { useFetch } from 'services/useFetch'
import { API_VOUCHER } from 'services/apiVoucher'
import moment from 'moment/moment'
import {
    capitalize,
    constructOptionMultipleSelect,
    constructOptionsSelect,
    convertErrorMessage,
    formatDateMoment,
    getSectionError,
    getValueFromArray,
    replaceAll,
    uploadFileToS3,
} from 'utils/utils'
import { SUBMIT_TYPE } from 'constants/constant'
import { Alert } from 'avo-ui'
import { INITIAL_STATE, GET_FIELD_NAME, FIELD_NAME_DETAIL, SECTION_FIELDS } from 'configs/voucher/configFormVoucher'

export const VoucherCreateContainer = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const voucherId = searchParams.get('voucherId')
    const [voucherDetail, setVoucherDetail] = useState(INITIAL_STATE)
    const [isLoadingUpload, setIsLoadingUpload] = useState(false)
    const { fetching: getVoucherDetail, isLoading } = useFetch(API_VOUCHER.DETAIL)
    const { fetching: getUrlThumbnail } = useFetch(API_VOUCHER.URL_THUMBNAIL)
    const { fetching: createVoucher, isLoading: isCreating } = useFetch(API_VOUCHER.CREATE)
    const { fetching: updateVoucher, isLoading: isUpdating } = useFetch(API_VOUCHER.UPDATE)
    const { fetching: draftVoucher, isLoading: isDrafting } = useFetch(API_VOUCHER.DRAFT)
    const [errorForm, setErrorForm] = useState({})
    const sectionRef = {
        general_settings: useRef(),
        restriction: useRef(),
        usage_limit: useRef(),
    }

    const onChangeForm = (item) => {
        setVoucherDetail({ ...voucherDetail, ...item })
    }

    const setShowAlert = (message) => {
        return Alert({
            icon: 'success',
            title: 'Success',
            message,
        })
    }

    const setUploadThumbnail = async (type) => {
        try {
            const s3Urls = await getUrlThumbnail({ filename: voucherDetail?.thumbnail?.name?.replace(/\s/g, '') })
            await uploadFileToS3(s3Urls?.data, voucherDetail?.thumbnail?.file)
            onSave(type, `${s3Urls.data.url}${s3Urls.data.fields.key}`)
        } catch (err) {
            console.log('upload failed')
        }

        setIsLoadingUpload(false)
    }

    const validateForm = () => {
        const copyRestriction = { ...voucherDetail?.restriction }
        const giftErrors = voucherDetail?.gifts?.map((item) => !item?.quantity && 'Quantity tidak boleh kosong')
        const isFreeGift = voucherDetail?.type?.value === 'free-gift'
        const isPercentage = voucherDetail?.discount_type === 'percentage'
        const isNominal = voucherDetail?.discount_type === 'nominal'
        const isAmount = (key) => {
            return (key === 'amount_percentage' && isPercentage) || (key === 'amount_nominal' && isNominal)
        }

        const isInvalid = {
            gift: giftErrors?.find((item) => item),
            time: moment(voucherDetail?.date_expires)?.isSameOrBefore(moment(voucherDetail?.date_start)),
            spend: Number(copyRestriction?.maximum_amount) < Number(copyRestriction?.minimum_amount),
        }

        return new Promise((resolve, reject) => {
            const errors = {}
            Object.keys(voucherDetail).forEach((key) => {
                if (GET_FIELD_NAME[key] && !voucherDetail[key]) {
                    errors[key] = `${GET_FIELD_NAME[key]} wajib diisi`
                } else if (key === 'date_expires' && isInvalid?.time) {
                    errors[key] = 'Jam berakhir tidak boleh kurang atau sama dengan jam mulai'
                } else if (isAmount(key) && !voucherDetail[key] && voucherDetail?.type && !isFreeGift) {
                    errors[key] = 'Nilai voucher wajib diisi'
                } else if (key === 'gifts' && !voucherDetail[key]?.length && isFreeGift) {
                    errors[key] = 'Gifts wajib diisi'
                } else if (key === 'gifts' && isInvalid?.gift && isFreeGift) {
                    errors['selected_gift'] = giftErrors
                } else if (voucherDetail[key]?.maximum_amount && isInvalid?.spend) {
                    errors[key] = { maximum_amount: 'Maksimum spend harus lebih besar dari minimum spend' }
                }
            })
            setErrorForm(errors)
            if (!Object.keys(errors).length) resolve()
        })
    }

    const onSubmit = (type) => {
        validateForm().then(async () => {
            if (voucherDetail?.thumbnail?.name) {
                setIsLoadingUpload(true)
                await setUploadThumbnail(type)
            } else {
                onSave(type)
            }
        })
    }

    const isCombineValidation = () => {
        const copyRestriction = { ...voucherDetail?.restriction }
        let isValid = false
        Object.keys(copyRestriction).forEach((key) => {
            if (
                Number(copyRestriction[key]) ||
                copyRestriction[key]?.length ||
                (copyRestriction[key] && typeof copyRestriction[key] === 'boolean')
            ) {
                isValid = true
            }
        })
        return isValid
    }

    const mapGifts = (items, isFreeShipping) => {
        return items?.map((i) => {
            return {
                gift_id: i?.value,
                quantity: i?.quantity,
                is_free_shipping: isFreeShipping,
            }
        })
    }

    const onSave = (type, url = voucherDetail?.thumbnail) => {
        const { is_free_shipping, amount_percentage, amount_nominal, restriction, ...payload } = { ...voucherDetail }
        const isFreeGift = payload?.type?.value === 'free-gift'
        const isPercentage = payload?.discount_type === 'percentage'
        const newPayload = {
            ...payload,
            thumbnail: url,
            type: payload?.type?.value,
            date_start: payload?.date_start ? moment(payload?.date_start).utc().format('YYYY-MM-DD HH:mm') : null,
            date_expires: payload?.date_expires ? moment(payload?.date_expires).utc().format('YYYY-MM-DD HH:mm') : null,
            gifts: isFreeGift ? mapGifts(payload?.gifts, is_free_shipping) : [],
            combine_validation: isCombineValidation(),
            amount: isPercentage ? amount_percentage : amount_nominal,
            max_amount: isPercentage ? payload?.max_amount : null,
            ...(isFreeGift && {
                discount_type: null,
                amount: null,
                max_amount: null,
            }),
            ...(isCombineValidation() && {
                restriction: {
                    ...restriction,
                    include_product_ids: getValueFromArray(restriction?.include_product_ids, 'value'),
                    exclude_product_ids: getValueFromArray(restriction?.exclude_product_ids, 'value'),
                    include_category_ids: getValueFromArray(restriction?.include_category_ids, 'value'),
                    exclude_category_ids: getValueFromArray(restriction?.exclude_category_ids, 'value'),
                    include_brand_ids: getValueFromArray(restriction?.include_brand_ids, 'value'),
                    exclude_brand_ids: getValueFromArray(restriction?.exclude_brand_ids, 'value'),
                    allowed_email: getValueFromArray(restriction?.allowed_email, 'value'),
                },
            }),
        }

        if (!voucherId && type === SUBMIT_TYPE?.PUBLISH) {
            createVoucher(newPayload)
                .then(() => {
                    setShowAlert('Voucher Created !')
                    navigate('/voucher/voucher-list')
                })
                .catch((error) => setErrorForm(convertErrorMessage(error?.data, FIELD_NAME_DETAIL)))
        } else if (type === SUBMIT_TYPE?.PUBLISH) {
            updateVoucher(newPayload, voucherId)
                .then(() => {
                    setShowAlert('Voucher Updated !')
                    navigate('/voucher/voucher-list')
                })
                .catch((error) => setErrorForm(convertErrorMessage(error?.data, FIELD_NAME_DETAIL)))
        } else {
            draftVoucher(newPayload, voucherId)
                .then(() => {
                    setShowAlert('Voucher Drafted !')
                    navigate('/voucher/voucher-list')
                })
                .catch((error) => setErrorForm(convertErrorMessage(error?.data, FIELD_NAME_DETAIL)))
        }
    }

    useEffect(() => {
        const topSection = getSectionError(errorForm, SECTION_FIELDS)[0]
        sectionRef?.[topSection]?.current?.scrollIntoView({ behavior: 'smooth' })
    }, [errorForm])

    useEffect(() => {
        if (voucherId) {
            getVoucherDetail({}, voucherId).then((response) => {
                const data = { ...INITIAL_STATE, ...response?.data }
                const restrict = data?.restriction
                const isPercentage = data?.discount_type === 'percentage'
                const newDetail = {
                    ...data,
                    type: { label: capitalize(replaceAll(data?.type, '-', ' ')), value: data?.type },
                    date_start: data?.date_start ? formatDateMoment(data?.date_start, 'YYYY-MM-DD HH:mm') : null,
                    date_expires: data?.date_expires ? formatDateMoment(data?.date_expires, 'YYYY-MM-DD HH:mm') : null,
                    gifts: constructOptionMultipleSelect(data?.gifts, 'name', 'id', null, 'quantity'),
                    is_free_shipping: !!data?.gifts?.find((i) => i?.is_free_shipping),
                    amount_percentage: isPercentage ? data?.amount : null,
                    amount_nominal: !isPercentage ? data?.amount : null,
                    restriction: {
                        ...restrict,
                        include_product_ids: constructOptionMultipleSelect(restrict?.include_product_ids, 'name', 'id'),
                        exclude_product_ids: constructOptionMultipleSelect(restrict?.exclude_product_ids, 'name', 'id'),
                        include_category_ids: constructOptionsSelect(restrict?.include_category_ids, 'name', 'id'),
                        exclude_category_ids: constructOptionsSelect(restrict?.exclude_category_ids, 'name', 'id'),
                        include_brand_ids: constructOptionsSelect(restrict?.include_brand_ids, 'name', 'id'),
                        exclude_brand_ids: constructOptionsSelect(restrict?.exclude_brand_ids, 'name', 'id'),
                        shipping_courier: getValueFromArray(restrict?.shipping_courier, 'alias'),
                        payment_method: getValueFromArray(restrict?.payment_method, 'alias'),
                        allowed_email: constructOptionsSelect(restrict?.allowed_email),
                    },
                }
                setVoucherDetail(newDetail)
            })
        } else {
            setVoucherDetail(INITIAL_STATE)
        }
        setErrorForm({})
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }, [voucherId])

    const props = {
        sectionProps: {
            sectionRef,
            voucherDetail,
            onChangeForm,
            errorForm,
        },
        isLoadingUpload,
        isLoading,
        isCreating,
        isUpdating,
        isDrafting,
        onSubmit,
    }

    return <VoucherCreate {...props} />
}
